import axios from "axios";

export const API_URL = "https://srinivasafleet.com/api";

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");

      if (window.location.pathname !== "/login") {
        window.location.href = "/login?reason=session_revoked";
      }
    }

    return Promise.reject(error);
  }
);

export const getVehicleAssignments = async (opts = {}) => {
  const params = {};

  if (opts.allYears) {
    params.all_years = true;
  } else if (opts.financialYearId) {
    params.financial_year_id = opts.financialYearId;
  }

  if (opts.assignmentTypeId) {
    params.assignment_type_id = opts.assignmentTypeId;
  }

  if (opts.supplierType) {
    params.supplier_type = opts.supplierType;
  }

  if (opts.vehicleModel) {
    params.vehicle_model = opts.vehicleModel;
  }

  if (opts.locationId) {
    params.location_id = opts.locationId;
  }

  if (opts.branchId) {
    params.branch_id = opts.branchId;
  }

  if (
    opts.outstandingMin !== undefined &&
    opts.outstandingMin !== null &&
    opts.outstandingMin !== ""
  ) {
    params.outstanding_min = opts.outstandingMin;
  }

  if (
    opts.outstandingMax !== undefined &&
    opts.outstandingMax !== null &&
    opts.outstandingMax !== ""
  ) {
    params.outstanding_max = opts.outstandingMax;
  }

  const response = await api.get("/vehicle-assignments/", {
    params,
  });

  return response.data;
};

export const getVehicleAssignmentById = async (id) => {
  const response = await api.get(`/vehicle-assignments/${id}`);
  return response.data;
};

export const getVehicleAssignmentDocuments = async (id) => {
  const response = await api.get(
    `/vehicle-assignments/${id}/documents`
  );

  return response.data;
};

export const getAssignmentChain = async (chainId) => {
  const response = await api.get(
    `/vehicle-assignments/chain/${chainId}`
  );

  return response.data;
};

export const getAssignmentTypeOptions = async () => {
  const response = await api.get(
    "/vehicle-assignments/filters/types"
  );

  return response.data;
};

export const getSupplierTypeOptions = async () => {
  const response = await api.get(
    "/vehicle-assignments/filters/supplier-types"
  );

  return response.data;
};

export const getVehicleModelOptions = async () => {
  const response = await api.get(
    "/vehicle-assignments/filters/models"
  );

  return response.data;
};

export const getLocationOptions = async () => {
  const response = await api.get(
    "/vehicle-assignments/filters/locations"
  );

  return response.data;
};

export const getBranchesByLocation = async (locationId) => {
  const response = await api.get(
    "/vehicle-assignments/filters/branches",
    {
      params: {
        location_id: locationId,
      },
    }
  );

  return response.data;
};

export const getAvailableVehicles = async () => {
  const response = await api.get(
    "/vehicle-assignments/available/vehicles"
  );

  return response.data;
};

export const getAvailableDrivers = async () => {
  const response = await api.get(
    "/vehicle-assignments/available/drivers"
  );

  return response.data;
};

export const getAvailableSuppliers = async () => {
  const response = await api.get(
    "/vehicle-assignments/available/suppliers"
  );

  return response.data;
};

export const createVehicleAssignment = async (data) => {
  const response = await api.post(
    "/vehicle-assignments/",
    data
  );

  return response.data;
};

export const updateVehicleAssignment = async (id, data) => {
  const response = await api.put(
    `/vehicle-assignments/${id}`,
    data
  );

  return response.data;
};

export const deleteVehicleAssignment = async (id) => {
  const response = await api.delete(
    `/vehicle-assignments/${id}`
  );

  return response.data;
};

export const uploadVehicleAssignmentDocument = async (
  field,
  file
) => {
  const formData = new FormData();

  formData.append("file", file);

  const response = await api.post(
    `/vehicle-assignments/upload-document?field=${encodeURIComponent(field)}`,
    formData
  );

  return response.data;
};

export const getVehicleAssignmentStatements = async (
  assignmentId,
  params = {}
) => {
  if (!assignmentId) {
    throw new Error("Vehicle assignment ID is required");
  }

  const response = await api.get(
    `/vehicle-assignments/${assignmentId}/statements`,
    { params }
  );

  return response.data;
};

export const uploadVehicleAssignmentStatement = async (
  assignmentId,
  file,
  month,
  year
) => {
  if (!assignmentId) {
    throw new Error("Vehicle assignment ID is required");
  }

  if (!file) {
    throw new Error("Statement file is required");
  }

  if (month === undefined || month === null || month === "") {
    throw new Error("Statement month is required");
  }

  if (year === undefined || year === null || year === "") {
    throw new Error("Statement year is required");
  }

  const formData = new FormData();

  formData.append("file", file);
  formData.append("statement_month", String(month));
  formData.append("statement_year", String(year));

  const response = await api.post(
    `/vehicle-assignments/${assignmentId}/statements`,
    formData
  );

  return response.data;
};

export const deleteVehicleAssignmentStatement = async (
  assignmentId,
  statementId
) => {
  if (!assignmentId) {
    throw new Error("Vehicle assignment ID is required");
  }

  if (!statementId) {
    throw new Error("Statement ID is required");
  }

  const response = await api.delete(
    `/vehicle-assignments/${assignmentId}/statements/${statementId}`
  );

  return response.data;
};

export const resolveVehicleAssignmentStatementUrl = (
  fileUrl
) => {
  if (!fileUrl) {
    return "";
  }

  if (/^https?:\/\//i.test(fileUrl)) {
    return fileUrl;
  }

  const path = fileUrl.startsWith("/")
    ? fileUrl
    : `/${fileUrl}`;

  return `http://127.0.0.1:8000${path}`;
};

export const downloadVehicleAssignmentStatement = async (
  assignmentId,
  statement
) => {
  if (!assignmentId) {
    throw new Error("Vehicle assignment ID is required");
  }

  if (!statement) {
    throw new Error("Statement information is required");
  }

  const fileUrl =
    statement.file_url ||
    statement.url ||
    statement.statement_url ||
    statement.path;

  if (!fileUrl) {
    throw new Error("Statement file URL is not available");
  }

  const url = resolveVehicleAssignmentStatementUrl(fileUrl);

  const requestUrl = url.startsWith(API_URL)
    ? url.slice(API_URL.length)
    : url;

  const response = await api.get(requestUrl, {
    responseType: "blob",
  });

  const blobUrl = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");

  link.href = blobUrl;
  link.download =
    statement.file_name ||
    statement.filename ||
    statement.original_filename ||
    `vehicle-assignment-statement-${statement.id || "file"}`;

  document.body.appendChild(link);
  link.click();
  link.remove();

  window.URL.revokeObjectURL(blobUrl);

  return true;
};

export default api;